import { EntityMethod } from '../bos/entity-method.js'

export type PureExecutionLocation = 'server' | 'client'
export type ExecutionLocation = PureExecutionLocation | 'serverAndClient'
export const ExecutionContextNames = [
	'server',
	'client',
	'serverAndClient',
	'calculation',
] as const
export type ExecutionContextName = (typeof ExecutionContextNames)[number]
const ExecutonContextLocations: Record<
	ExecutionContextName,
	ExecutionLocation
> = {
	server: 'server',
	client: 'client',
	serverAndClient: 'serverAndClient',
	calculation: 'serverAndClient',
}

export const SysModuleNames: Record<ExecutionLocation, string> = {
	client: '@lg/ng-shared/frontend-sys.js',
	server: '@lg/backend/system/backend-sys.js',
	serverAndClient: '@lg/shared/system/common-sys.js',
}

export class ExecutionContext {
	constructor(
		public readonly contextName: ExecutionContextName,
		public readonly contextSuffixes?: string[],
	) {}

	get contextNameWithSuffixes() {
		return this.contextSuffixes
			? `${this.contextName}_${this.contextSuffixes.join('#')}`
			: `${this.contextName}`
	}

	private isOneOf(contextNames: ExecutionContextName[] | '*') {
		if (contextNames == '*') return true

		return contextNames.includes(this.contextName)
	}

	locationIsOneOf(locations: ExecutionLocation[]) {
		const location = this.getExecutionLocation()
		return locations.includes(location)
	}

	getExecutionLocation(): ExecutionLocation {
		return ExecutonContextLocations[this.contextName]
	}

	getSysModuleName() {
		return SysModuleNames[this.getExecutionLocation()]
	}

	canRunOnServer() {
		return this.locationIsOneOf(['server', 'serverAndClient'])
	}

	canRunOnClient() {
		return this.locationIsOneOf(['client', 'serverAndClient'])
	}

	needsClientDataStoreDeclarations() {
		return this.isOneOf(['calculation', 'client'])
	}

	needsServerDataStoreDeclarations() {
		return this.isOneOf(['server', 'client', 'serverAndClient'])
	}

	needsServiceIntegrationDeclarations() {
		return this.isOneOf(['server'])
	}

	needsServiceProviderDeclarations() {
		return this.isOneOf(['server'])
	}

	needsAppStateDeclaration() {
		return this.isOneOf(['client'])
	}

	needsScreenStateDeclaration() {
		return this.isOneOf(['client'])
	}

	needsAsyncForClientCallableMethods() {
		return this.isOneOf(['client', 'serverAndClient'])
	}

	needsUserDefinedConfigurationDeclarations() {
		return this.isOneOf(['server', 'client', 'serverAndClient'])
	}

	isEntityMethodAvailable(method: EntityMethod) {
		switch (method.availability) {
			case 'calculation':
				return true

			case 'serverOnly':
				return this.isOneOf(['server'])

			case 'clientOnly':
				return this.isOneOf(['client'])

			case 'serverAndClient':
				if (this.contextName == 'calculation') {
					return !method.isAsync
				}
				return this.isOneOf(['server', 'client', 'serverAndClient'])

			case 'callableFromClient':
				return this.isOneOf(['server', 'client', 'serverAndClient'])
		}
	}

	needsStaticEntityDeclarations() {
		return true
	}
}

export const AllExecutionContexts = ExecutionContextNames.map(
	(ecName) => new ExecutionContext(ecName),
)
